import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const isConfigured = Boolean(SENTRY_DSN && SENTRY_DSN !== 'your_sentry_dsn');

export const initSentry = () => {
  if (!isConfigured) {
    if (process.env.NODE_ENV !== 'production') {
      console.info('[Sentry] REACT_APP_SENTRY_DSN is not configured. Sentry tracking is disabled.');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_NODE_ENV || process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_VERSION ? `temple-client@${process.env.REACT_APP_VERSION}` : undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Tracing sample rate (capture 20% in production, 100% in development)
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.2 : 1.0,
    // Session Replays
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  console.log('✅ Sentry initialized on frontend');
};

/**
 * Associate the authenticated user with Sentry error logs & performance traces
 */
export const setSentryUser = (user) => {
  if (!isConfigured) return;

  if (user) {
    Sentry.setUser({
      id: String(user.id || user._id || ''),
      email: user.email,
      username: user.name || user.email,
      role: user.role,
    });
    // Set tag for convenient filtering of admin vs devotee errors
    Sentry.setTag('user_role', user.role || 'devotee');
    Sentry.setTag('is_admin', ['admin', 'super_admin', 'sub_admin'].includes(user.role) ? 'true' : 'false');
  } else {
    Sentry.setUser(null);
    Sentry.setTag('user_role', 'anonymous');
    Sentry.setTag('is_admin', 'false');
  }
};

/**
 * Track specific admin operations and critical events
 */
export const trackAdminAction = (actionName, metadata = {}) => {
  if (!isConfigured) return;

  Sentry.addBreadcrumb({
    category: 'admin_action',
    message: actionName,
    data: metadata,
    level: 'info',
  });
};

/**
 * Capture custom error with optional context
 */
export const captureAppError = (error, context = {}) => {
  if (!isConfigured) {
    console.error('[Error Tracker]:', error, context);
    return;
  }

  Sentry.withScope((scope) => {
    if (context.tags) {
      scope.setTags(context.tags);
    }
    if (context.extra) {
      scope.setExtras(context.extra);
    }
    if (context.level) {
      scope.setLevel(context.level);
    }
    Sentry.captureException(error);
  });
};

export default Sentry;
